import { useEffect, useRef, type ReactNode } from 'react';

interface PillTabsProps<T extends string> {
  tabs: Array<{ id: T; label: string; icon?: ReactNode }>;
  active: T;
  onChange: (id: T) => void;
  ariaLabel: string;
  className?: string;
}

/**
 * Pill-shaped tab strip. Keeps the original `tablist`/`tab` roles and
 * `aria-selected` semantics so existing keyboard and test interactions hold.
 */
export function PillTabs<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
  className = '',
}: PillTabsProps<T>) {
  const activeRef = useRef<HTMLButtonElement | null>(null);

  // Scroll the selected pill into the strip without scrolling the page.
  useEffect(() => {
    const el = activeRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [active]);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={[
        'inline-flex items-center gap-1 rounded-pill p-1',
        'glass-strong',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {tabs.map((tab) => {
        const selected = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${tab.id}-tab`}
            ref={selected ? activeRef : undefined}
            aria-selected={selected}
            aria-controls={`${tab.id}-panel`}
            onClick={() => onChange(tab.id)}
            className={[
              'inline-flex items-center gap-2 rounded-pill px-4 py-2 text-sm font-semibold',
              'transition-[background-color,color,box-shadow] duration-200 ease-out',
              selected
                ? 'bg-accent text-white shadow-card'
                : 'text-ink-soft hover:bg-surface/70 hover:text-ink',
            ].join(' ')}
          >
            {tab.icon ? (
              <span aria-hidden="true" className="[&>svg]:h-4 [&>svg]:w-4">
                {tab.icon}
              </span>
            ) : null}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
